// Single registry for the `events` collection types.
//
// Every consumer reads from here: Timeline renderer and tooltip, the fleet
// "Eventi" tab, the control dashboard, the notification center and the event
// form. Adding a type used to mean editing four parallel maps (`eventsMap`, two
// copies of `EVENT_VARIANT`, and the form's `typeOptions`), which is why they had
// already drifted — `pause` and `vehicle_assignment` were missing from the form.
//
// Per entry:
// - `color`    hex/CSS color for the Timeline item background. It is applied as
//              an inline style, so a `var(--token)` value is fine here (unlike
//              MUI x-charts series colors, which need a resolved hex).
// - `icon`     bare FontAwesome 6 glyph name — consumers prefix `fa-solid fa-`.
//              Free-set only: a Pro-only glyph renders empty.
// - `variant`  AppStatusChip color family.
// - `system`   true when the type is not user-selectable in the event form:
//              it is written by another flow (an assignment marker, or a record
//              in another collection mirrored onto the Timeline).
//
// Quanto un tipo pesa come "attività svolta" NON sta qui: sta in
// `driverScoreWeights.activityEventWeights`. Questo registro dice cos'è un
// evento, la configurazione del punteggio dice quanto vale — vedi
// `driverActivityWeight` in fondo.
import { DRIVER_SCORE_WEIGHTS } from '@/constants/driverScoreWeights';

const EVENT_TYPES = {
  pause: { color: '#ffc107', icon: 'pause', variant: 'warning', system: true },
  vehicle_cleaning: {
    color: '#28a745',
    icon: 'broom',
    variant: 'success',
  },
  vehicle_charge: {
    color: '#17a2b8',
    icon: 'battery-full',
    variant: 'info',
  },
  vehicle_maintenance: {
    color: '#dc3545',
    icon: 'wrench',
    variant: 'danger',
  },
  // `course` resta fuori dalle attività: è formazione **ricevuta**, non lavoro
  // svolto, ed è già tracciata in `driver.courses[]`.
  course: { color: '#6f42c1', icon: 'book', variant: 'purple' },
  // I viaggi a vuoto non sono attività: sono spostamento, e sono già dentro
  // l'occupazione (OCC-10). Contarli premierebbe il tempo perso.
  andata_a_vuoto: { color: '#fd7e14', icon: 'arrow-right', variant: 'warning' },
  ritorno_a_vuoto: { color: '#e83e8c', icon: 'arrow-left', variant: 'pink' },
  flyering: { color: '#946e78', icon: 'paper-plane', variant: 'pink' },
  // `other` è troppo vago per pesare su un punteggio con conseguenze: diventerebbe
  // il tipo che si sceglie quando serve un punto in più.
  other: { color: '#6c757d', icon: 'question', variant: 'neutral' },
  // Mirror of a `vehicle_damages` record (`damage_id` back reference): the damage
  // is reported from the vehicle card, not from the event form.
  vehicle_damage: { color: '#b02a37', icon: 'car-burst', variant: 'danger', system: true },
  // Mirror of an open maintenance practice (`process_id`). Its date follows the
  // scheduled workshop slot once known, and it leaves the Timeline when the
  // practice closes — dispatch only cares about what is still in flight.
  vehicle_maintenance_process: {
    color: '#7a4b00',
    icon: 'screwdriver-wrench',
    variant: 'warning',
    system: true,
  },
  // Mirror of a `refuelings` record (`refueling_id`). Created from the vehicle
  // card, from the Timeline context menu, or by a provider import.
  vehicle_refuel: { color: '#8a6d1f', icon: 'gas-pump', variant: 'warning', system: true },
  // Mirror of a *closed* `vehicle_inspections` record (`inspection_id`): the
  // monthly survey. A draft never reaches the Timeline — it is work in progress.
  vehicle_inspection: {
    color: '#2f6f4f',
    icon: 'clipboard-check',
    variant: 'success',
    system: true,
  },
  // Marker of the moment a vehicle was assigned to the driver (from
  // driver_vehicle_assignments, not from the events collection).
  vehicle_assignment: { color: 'var(--color-primary)', icon: 'key', variant: 'info', system: true },
};

export { EVENT_TYPES };

// Back-compat alias: the Timeline renderer, tooltip, modal, control dashboard
// and notification center all read `eventsMap[type].color / .icon`.
export const eventsMap = EVENT_TYPES;

/**
 * DPS-15 — quanto pesa questo tipo di evento come **attività svolta**.
 *
 * Il peso vive in `driverScoreWeights.activityEventWeights`, non qui: questo
 * registro descrive *cos'è* un tipo di evento (colore, icona, se è di sistema),
 * la configurazione del punteggio descrive *quanto vale*. Prima era un flag
 * `driver_activity` su ogni voce, che mescolava le due cose.
 *
 * I tipi `system` valgono **sempre 0**, qualunque cosa dica la configurazione:
 * sono specchi di record di altre collection (rifornimenti, danni, censimenti)
 * e conterebbero due volte la stessa cosa. È una regola del registro, non una
 * scelta di taratura, quindi resta qui.
 */
export const driverActivityWeight = (type) => {
  if (!type || EVENT_TYPES[type]?.system) return 0;
  return DRIVER_SCORE_WEIGHTS.activityEventWeights[type] ?? 0;
};

/** Tipi che contano come attività svolta (peso > 0). */
export const DRIVER_ACTIVITY_EVENT_TYPES = Object.keys(EVENT_TYPES).filter(
  (key) => driverActivityWeight(key) > 0
);

/** L'evento è un'attività svolta che pesa sul Performance Index? */
export const isDriverActivityEvent = (event) => driverActivityWeight(event?.type) > 0;

/** AppStatusChip color family for an event type. */
export const eventVariant = (type) => EVENT_TYPES[type]?.variant || 'neutral';

/** Bare FA glyph for an event type, with a safe fallback. */
export const eventIcon = (type, fallback = 'rectangle-list') => EVENT_TYPES[type]?.icon || fallback;

/**
 * Options for the event form's type select — only the types an operator can
 * pick. `system` types are written by other flows and must not be creatable by
 * hand, otherwise a hand-made record would have no counterpart in its own
 * collection.
 *
 * @param {(key: string) => string} translate label resolver (i18n.t)
 */
export const eventTypeOptions = (translate) =>
  Object.keys(EVENT_TYPES)
    .filter((key) => !EVENT_TYPES[key].system)
    .map((value) => ({ value, label: translate(value) }));
